import { FileDownload } from "@components/atoms/FileDownload"
import { defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { startTransition, useEffect, useMemo, useRef, useState } from "react"
import { MediaReleaseFragment } from "@generated-types"
import { ButtonGroup } from "@components/ui/ButtonGroup"
import { Select } from "@components/ui/Select"
import { ScrollSensor } from "@components/util/ScrollSensor"

export const meta: BlockMeta = {
  title: "Media Release Listing",
  icon: "download",
  category: "media",
  description: "Embed a list of media releases",
  tags: ["#subpage"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("media-releases/media-release-listing", (props) => {
  const [year, setYear] = useState<string>("all")
  const [page, setPage] = useState(2)

  const byYear = useMemo(() => {
    const byYear = new Map<string, MediaReleaseFragment[]>()
    for (const item of props.mediaReleases?.nodes ?? []) {
      const year = item.date?.substring(0, 4)
      if (year) {
        const yearItems = byYear.get(year) ?? byYear.set(year, []).get(year)!
        yearItems.push(item)
      }
    }
    return byYear
  }, [props.mediaReleases?.nodes])

  const pages = useMemo(() => {
    const pages = [...byYear.entries()]
      .map(([year, items]) => {
        return {
          year,
          content: (
            <div key={year}>
              <div className="type-title-m mb-5">{year}</div>
              <div className="flex flex-col gap-4 pb-8">
                {items.map((item, i) => (
                  <FileDownload
                    date={item.date!}
                    title={item.title}
                    key={i}
                    size={item.mediaReleaseInfo?.fileDownload?.fileSize!}
                    url={item.mediaReleaseInfo?.fileDownload?.mediaItemUrl!}
                    target="_blank"
                  />
                ))}
              </div>
            </div>
          ),
        }
      })
      .filter((item) => year === "all" || item.year === year)

    return pages
  }, [byYear, year])

  return (
    <div>
      <div className="grid-auto">
        <div className="col-span-12 sm:col-2-8 md:col-span-6 mb-6">
          <ButtonGroup className="sticky top-headerHeight -mt-4 pt-4">
            <Select
              heading="Filter by year"
              placeholder="Filter by year"
              items={[
                {
                  label: "All",
                  value: "all",
                },
                ...(byYear?.keys?.().map?.((year) => ({ label: year, value: year })) || []),
              ]}
              onValueChange={(value) => {
                startTransition(() => {
                  setPage(1)
                  if (value) {
                    setYear(value)
                  }
                })
              }}
            />
          </ButtonGroup>
        </div>
        <div className="col-subpage-content-grid">
          {pages.map((item, index) => {
            if (index > page) return null
            return item.content
          })}
          {/* If the user scrolls down here, display the next page */}
          {page < pages.length && (
            <ScrollSensor
              onActivate={() => {
                setPage(page + 1)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
})
